import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CategoryService } from '@admin-core/services/category.service';
import { FilterService } from '@admin-core/services/filter.service';
import {
  Filter,
  FilterFacet,
  FilterOptionRequest,
  FilterValueType
} from '@admin-core/models/filter.model';
import { LanguageService } from '@admin-core/i18n/language.service';
import { TranslatePipe } from '@admin-core/i18n/translate.pipe';
import { ConfirmDialogService } from '@admin-shared/components/confirm-dialog/confirm-dialog.service';

interface OptionDraft {
  id?: string | null;
  label: string;
  value: string;
  displayOrder: number;
}

@Component({
  selector: 'app-category-filters-admin',
  standalone: true,
  imports: [ReactiveFormsModule, FormsModule, TranslatePipe],
  templateUrl: './category-filters-admin.component.html',
  styleUrl: './category-filters-admin.component.scss'
})
export class CategoryFiltersAdminComponent implements OnInit {
  private readonly categoriesApi = inject(CategoryService);
  private readonly filtersApi = inject(FilterService);
  private readonly fb = inject(FormBuilder);
  private readonly i18n = inject(LanguageService);
  private readonly confirmDialog = inject(ConfirmDialogService);

  readonly leafCategories = signal<{ id: string; name: string; parentName: string }[]>([]);
  readonly allFilters = signal<Filter[]>([]);
  readonly assignedFacets = signal<FilterFacet[]>([]);
  readonly selectedCategoryId = signal<string | null>(null);
  readonly loading = signal(false);
  readonly filterDialogOpen = signal(false);
  readonly editingFilterId = signal<string | null>(null);
  readonly optionDrafts = signal<OptionDraft[]>([]);
  readonly selectedValueType = signal<FilterValueType>('Text');

  readonly valueTypes: FilterValueType[] = ['Text', 'Number', 'Boolean', 'Options'];

  readonly assignedFilters = computed(() => this.assignedFacets());

  readonly availableFilters = computed(() => {
    const ids = new Set(this.assignedFacets().map((f) => f.id));
    return this.allFilters().filter((f) => !ids.has(f.id) && f.isActive);
  });

  readonly isOptionsType = computed(() => this.selectedValueType() === 'Options');

  readonly filterForm = this.fb.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(150)]],
    valueType: this.fb.nonNullable.control<FilterValueType>('Text', Validators.required),
    isActive: [true]
  });

  ngOnInit(): void {
    this.loadBase();
  }

  loadBase(): void {
    this.loading.set(true);

    this.categoriesApi.getTree().subscribe({
      next: (tree) => {
        const leaves: { id: string; name: string; parentName: string }[] = [];
        for (const parent of tree) {
          for (const child of parent.children ?? []) {
            leaves.push({ id: child.id, name: child.name, parentName: parent.name });
          }
        }
        this.leafCategories.set(leaves);
        if (!this.selectedCategoryId() && leaves.length > 0) {
          this.selectCategory(leaves[0].id);
        }
      },
      error: (err) =>
        this.showError(err?.error?.error ?? this.i18n.t('filters.loadCategoriesError'))
    });

    this.filtersApi.getAll().subscribe({
      next: (filters) => {
        this.allFilters.set(filters);
        this.loading.set(false);
      },
      error: (err) => {
        this.showError(err?.error?.error ?? this.i18n.t('filters.loadError'));
        this.loading.set(false);
      }
    });
  }

  selectCategory(categoryId: string): void {
    this.selectedCategoryId.set(categoryId);
    this.filtersApi.getCategoryFilters(categoryId).subscribe({
      next: (res) => {
        const facets = [...(res.filters ?? [])].sort(
          (a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name)
        );
        this.assignedFacets.set(facets);
      },
      error: (err) =>
        this.showError(err?.error?.error ?? this.i18n.t('filters.loadCategoryError'))
    });
  }

  moveAssigned(index: number, direction: -1 | 1): void {
    const items = [...this.assignedFacets()];
    const target = index + direction;
    if (target < 0 || target >= items.length) {
      return;
    }
    [items[index], items[target]] = [items[target], items[index]];
    this.assignedFacets.set(items);
  }

  saveAssignedOrder(): void {
    const categoryId = this.selectedCategoryId();
    if (!categoryId) {
      return;
    }

    this.filtersApi
      .setCategoryFilterOrder(
        categoryId,
        this.assignedFacets().map((f) => f.id)
      )
      .subscribe({
        next: () => this.selectCategory(categoryId),
        error: (err) => this.showError(err?.error?.error ?? this.i18n.t('filters.orderError'))
      });
  }

  assign(filterId: string): void {
    const categoryId = this.selectedCategoryId();
    if (!categoryId) {
      return;
    }

    this.filtersApi.assignToCategory(categoryId, filterId).subscribe({
      next: () => this.selectCategory(categoryId),
      error: (err) => this.showError(err?.error?.error ?? this.i18n.t('filters.assignError'))
    });
  }

  unassign(filterId: string, filterName: string): void {
    const categoryId = this.selectedCategoryId();
    if (!categoryId) {
      return;
    }
    this.filtersApi.getCategoryFilterUsage(categoryId, filterId).subscribe({
      next: async (usage) => {
        const message = usage.hasDependencies
          ? this.i18n.t('filters.unassignConfirmUsed', {
              name: usage.filterName || filterName,
              values: usage.categoryValueCount,
              products: usage.productCount,
              bundles: usage.bundleCount
            })
          : this.i18n.t('filters.unassignConfirmSimple', {
              name: usage.filterName || filterName
            });

        const ok = await this.confirmDialog.confirm({
          title: this.i18n.t('common.confirmTitle'),
          message,
          confirmLabel: this.i18n.t('filters.remove'),
          danger: true
        });
        if (!ok) {
          return;
        }

        this.filtersApi.unassignFromCategory(categoryId, filterId).subscribe({
          next: () => this.selectCategory(categoryId),
          error: (err) =>
            this.showError(err?.error?.error ?? this.i18n.t('filters.unassignError'))
        });
      },
      error: (err) =>
        this.showError(err?.error?.error ?? this.i18n.t('filters.unassignError'))
    });
  }

  openCreateFilter(): void {
    this.editingFilterId.set(null);
    this.filterForm.reset({ name: '', valueType: 'Text', isActive: true });
    this.filterForm.controls.valueType.enable();
    this.selectedValueType.set('Text');
    this.optionDrafts.set([{ id: null, label: '', value: '', displayOrder: 0 }]);
    this.filterDialogOpen.set(true);
  }

  openEditFilter(filter: Filter): void {
    this.editingFilterId.set(filter.id);
    const valueType = this.normalizeValueType(filter.valueType);
    this.filterForm.setValue({
      name: filter.name,
      valueType,
      isActive: filter.isActive
    });
    this.filterForm.controls.valueType.disable();
    this.selectedValueType.set(valueType);
    this.optionDrafts.set(
      valueType === 'Options'
        ? (filter.options ?? []).map((o, i) => ({
            id: o.id,
            label: o.label,
            value: o.value,
            displayOrder: o.displayOrder ?? i
          }))
        : []
    );
    this.filterDialogOpen.set(true);
  }

  closeFilterDialog(): void {
    this.filterDialogOpen.set(false);
    this.filterForm.controls.valueType.enable();
    this.optionDrafts.set([]);
  }

  onValueTypeChange(): void {
    const valueType = this.normalizeValueType(this.filterForm.controls.valueType.value);
    this.selectedValueType.set(valueType);
    if (valueType === 'Options' && this.optionDrafts().length === 0) {
      this.optionDrafts.set([{ id: null, label: '', value: '', displayOrder: 0 }]);
    }
  }

  addOption(): void {
    this.optionDrafts.update((items) => [
      ...items,
      { id: null, label: '', value: '', displayOrder: items.length }
    ]);
  }

  removeOption(index: number): void {
    this.optionDrafts.update((items) => items.filter((_, i) => i !== index));
  }

  setOptionField(index: number, field: 'label' | 'value', value: string): void {
    this.optionDrafts.update((items) =>
      items.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  }

  saveFilter(): void {
    if (this.filterForm.invalid) {
      this.filterForm.markAllAsTouched();
      return;
    }

    const raw = this.filterForm.getRawValue();
    const valueType = this.normalizeValueType(raw.valueType);
    const options = this.buildOptionsPayload(valueType);

    if (valueType === 'Options' && options.length === 0) {
      this.showError(this.i18n.t('filters.optionsRequired'));
      return;
    }

    const id = this.editingFilterId();

    if (id) {
      this.filtersApi
        .update(id, {
          name: raw.name,
          isActive: raw.isActive,
          options: valueType === 'Options' ? options : undefined
        })
        .subscribe({
          next: () => {
            this.closeFilterDialog();
            this.loadBase();
          },
          error: (err) =>
            this.showError(err?.error?.error ?? this.i18n.t('filters.updateError'))
        });
      return;
    }

    this.filtersApi
      .create({
        name: raw.name,
        valueType,
        isActive: raw.isActive,
        options: valueType === 'Options' ? options : undefined
      })
      .subscribe({
        next: () => {
          this.closeFilterDialog();
          this.loadBase();
        },
        error: (err) => this.showError(err?.error?.error ?? this.i18n.t('filters.createError'))
      });
  }

  async removeFilter(filter: Filter): Promise<void> {
    if (filter.isSystem) {
      this.showError(this.i18n.t('filters.systemDeleteError'));
      return;
    }

    const ok = await this.confirmDialog.confirm({
      title: this.i18n.t('common.confirmTitle'),
      message: this.i18n.t('filters.deleteConfirm', { name: filter.name }),
      confirmLabel: this.i18n.t('common.delete'),
      danger: true
    });
    if (!ok) {
      return;
    }

    this.filtersApi.delete(filter.id).subscribe({
      next: () => this.loadBase(),
      error: (err) => this.showError(err?.error?.error ?? this.i18n.t('filters.deleteError'))
    });
  }

  valueTypeLabel(value: FilterValueType | number): string {
    return this.normalizeValueType(value);
  }

  private buildOptionsPayload(valueType: FilterValueType): FilterOptionRequest[] {
    if (valueType !== 'Options') {
      return [];
    }

    return this.optionDrafts()
      .map((o, i) => ({
        id: o.id || undefined,
        label: o.label.trim(),
        value: o.value.trim() || undefined,
        displayOrder: o.displayOrder ?? i
      }))
      .filter((o) => o.label.length > 0);
  }

  private normalizeValueType(value: FilterValueType | number): FilterValueType {
    if (typeof value === 'string') {
      return value;
    }

    const map: FilterValueType[] = ['Guid', 'Text', 'Number', 'Boolean', 'Options'];
    return map[value] ?? 'Text';
  }

  private showError(message: string): void {
    void this.confirmDialog.alert({
      title: this.i18n.t('common.errorTitle'),
      message,
      danger: true
    });
  }
}
